import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ACCENT, SURFACE, cardSx } from "./assessmentTheme";
import { GROUPS, TAB_ICONS } from "./assessmentNav";

const RAIL_BG = "#eef3fb"; // light blue tint behind the menu
const SELECTED_BG = "#2f64c4"; // medium blue for the selected tab

export const RAIL_WIDTH = 224;
export const RAIL_WIDTH_COLLAPSED = 68;

const groupedTabs = (tabs) =>
  GROUPS.map((g) => ({
    ...g,
    items: tabs
      .map((tab, index) => ({ tab, index }))
      .filter(({ tab }) => tab.group === g.key),
  })).filter((g) => g.items.length > 0);

function RailItem({ label, icon, selected, collapsed, onClick }) {
  const button = (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      aria-current={selected ? "page" : undefined}
      sx={{
        position: "relative",
        borderRadius: "10px",
        mb: 0.25,
        py: 0.9,
        px: collapsed ? 0 : 1.5,
        justifyContent: collapsed ? "center" : "flex-start",
        color: "#4b5565",
        "& .MuiListItemIcon-root": {
          minWidth: collapsed ? 0 : 36,
          color: "inherit",
        },
        "& svg": { fontSize: 20 },
        "&:hover": { backgroundColor: "rgba(23,71,158,0.10)" },
        "&.Mui-selected": {
          color: "#fff",
          backgroundColor: SELECTED_BG,
          boxShadow: "0 2px 6px rgba(47,100,196,0.25)",
          "&:hover": { backgroundColor: ACCENT },
        },
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: "0.86rem",
            fontWeight: selected ? 700 : 500,
            noWrap: true,
          }}
        />
      )}
    </ListItemButton>
  );
  return collapsed ? (
    <Tooltip title={label} placement="right" arrow>
      {button}
    </Tooltip>
  ) : (
    button
  );
}

RailItem.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.node,
  selected: PropTypes.bool,
  collapsed: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

// Desktop: vertical rail grouped by section, collapsible to icons.
export function AssessmentRail({ tabs, value, onSelect, collapsed, onToggle }) {
  return (
    <Box
      component="nav"
      aria-label="Assessment sections"
      sx={{
        ...cardSx,
        background: RAIL_BG,
        width: collapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH,
        flexShrink: 0,
        p: 1,
        position: "sticky",
        top: 8,
        alignSelf: "flex-start",
        maxHeight: "calc(100vh - 24px)",
        overflowY: "auto",
        transition: "width 0.2s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 0 : 1,
          pb: 0.5,
        }}
      >
        {!collapsed && (
          <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#1a1a2e" }}>
            Assessment
          </Typography>
        )}
        <Tooltip title={collapsed ? "Expand menu" : "Collapse menu"} placement="right">
          <IconButton size="small" onClick={onToggle} aria-label="toggle assessment menu">
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {groupedTabs(tabs).map((group) => (
        <Box key={group.key} sx={{ mt: 1 }}>
          {collapsed ? (
            <Box sx={{ height: "1px", backgroundColor: SURFACE.border, mx: 1.5, mb: 1 }} />
          ) : (
            <Typography
              sx={{
                px: 1.5,
                pb: 0.5,
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: "uppercase",
                color: "#98a2b3",
              }}
            >
              {group.label}
            </Typography>
          )}
          <List disablePadding>
            {group.items.map(({ tab, index }) => (
              <RailItem
                key={tab.label}
                label={tab.label}
                icon={TAB_ICONS[tab.label]}
                selected={index === value}
                collapsed={collapsed}
                onClick={() => onSelect(index)}
              />
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}

AssessmentRail.propTypes = {
  tabs: PropTypes.array.isRequired,
  value: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  collapsed: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

// Small screens: a single "Section · Screen" button that opens a grouped menu.
export function AssessmentMenuButton({ tabs, value, onSelect }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const current = tabs[value];
  const groupLabel = GROUPS.find((g) => g.key === current?.group)?.label;

  return (
    <>
      <Button
        fullWidth
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<KeyboardArrowDownIcon />}
        startIcon={TAB_ICONS[current?.label]}
        sx={{
          ...cardSx,
          justifyContent: "flex-start",
          textTransform: "none",
          color: ACCENT,
          fontWeight: 700,
          py: 1,
          "& .MuiButton-endIcon": { ml: "auto" },
        }}
      >
        {groupLabel} · {current?.label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { maxHeight: "70vh", minWidth: 240, borderRadius: 3 } }}
      >
        {groupedTabs(tabs).flatMap((group) => [
          <ListSubheader key={`h-${group.key}`} sx={{ lineHeight: "32px", fontWeight: 700 }}>
            {group.label}
          </ListSubheader>,
          ...group.items.map(({ tab, index }) => (
            <MenuItem
              key={tab.label}
              selected={index === value}
              onClick={() => {
                setAnchorEl(null);
                onSelect(index);
              }}
              sx={{
                gap: 1.5,
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: SELECTED_BG,
                  color: "#fff",
                },
              }}
            >
              {TAB_ICONS[tab.label]}
              {tab.label}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
}

AssessmentMenuButton.propTypes = {
  tabs: PropTypes.array.isRequired,
  value: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
};
